import type {
  Marketplace,
  MarketplaceAccount,
  MarketplaceCapabilities,
  MarketplaceConnectionProfile
} from "./types";

export interface MarketplaceProfileDefinition {
  id: MarketplaceConnectionProfile;
  marketplace: Marketplace;
  label: string;
  description: string;
  direct: boolean;
  requiresApproval: boolean;
  capabilities: MarketplaceCapabilities;
}

const guidedCapabilities: MarketplaceCapabilities = {
  listingDrafts: true,
  directPublishing: false,
  inventorySync: false,
  orderSync: false,
  fulfilmentSync: false,
  shippingLabels: false,
  refunds: false
};

export const marketplaceProfileDefinitions: MarketplaceProfileDefinition[] = [
  {
    id: "ebay_api",
    marketplace: "ebay",
    label: "Connect eBay",
    description: "Import listings, stock, and orders. Connecting does not change eBay.",
    direct: true,
    requiresApproval: false,
    capabilities: {
      listingDrafts: true,
      directPublishing: true,
      inventorySync: true,
      orderSync: true,
      fulfilmentSync: true,
      shippingLabels: false,
      refunds: true
    }
  },
  {
    id: "etsy_api",
    marketplace: "etsy",
    label: "Direct connection",
    description: "Connect an Etsy shop to keep listings, stock, and orders together.",
    direct: true,
    requiresApproval: false,
    capabilities: {
      listingDrafts: true,
      directPublishing: true,
      inventorySync: true,
      orderSync: true,
      fulfilmentSync: false,
      shippingLabels: false,
      refunds: false
    }
  },
  {
    id: "shopify_api",
    marketplace: "shopify",
    label: "Direct connection",
    description: "Connect a Shopify store to keep products, stock, and orders together.",
    direct: true,
    requiresApproval: false,
    capabilities: {
      listingDrafts: true,
      directPublishing: true,
      inventorySync: true,
      orderSync: true,
      fulfilmentSync: true,
      shippingLabels: false,
      refunds: true
    }
  },
  {
    id: "depop_standard",
    marketplace: "depop",
    label: "Manual listing",
    description: "Prepare listing copy and photos, then publish them on Depop.",
    direct: false,
    requiresApproval: false,
    capabilities: guidedCapabilities
  },
  {
    id: "depop_partner",
    marketplace: "depop",
    label: "Depop Partner API",
    description: "Partner OAuth for listings, orders, shipping, refunds and offer management.",
    direct: true,
    requiresApproval: true,
    capabilities: {
      listingDrafts: true,
      directPublishing: true,
      inventorySync: true,
      orderSync: true,
      fulfilmentSync: true,
      shippingLabels: true,
      refunds: true
    }
  },
  {
    id: "vinted_standard",
    marketplace: "vinted",
    label: "Manual listing",
    description: "Prepare listing copy and photos, then publish them on Vinted.",
    direct: false,
    requiresApproval: false,
    capabilities: guidedCapabilities
  },
  {
    id: "vinted_pro",
    marketplace: "vinted",
    label: "Vinted Pro",
    description: "For businesses already approved by Vinted Pro. Connect stock and orders automatically, with shipping support where available.",
    direct: true,
    requiresApproval: true,
    capabilities: {
      listingDrafts: true,
      directPublishing: true,
      inventorySync: true,
      orderSync: true,
      fulfilmentSync: true,
      shippingLabels: true,
      refunds: false
    }
  },
  {
    id: "guided",
    marketplace: "etsy",
    label: "Guided handoff",
    description: "Prepare channel-specific copy and complete publication in the marketplace.",
    direct: false,
    requiresApproval: false,
    capabilities: guidedCapabilities
  }
];

const defaultProfiles: Record<Marketplace, MarketplaceConnectionProfile> = {
  ebay: "ebay_api",
  etsy: "etsy_api",
  shopify: "shopify_api",
  depop: "depop_standard",
  vinted: "vinted_standard",
  other: "guided"
};

export function defaultMarketplaceProfile(marketplace: Marketplace) {
  return defaultProfiles[marketplace];
}

export function marketplaceProfilesFor(marketplace: Marketplace) {
  const profiles = marketplaceProfileDefinitions.filter(
    (profile) => profile.marketplace === marketplace && profile.id !== "guided"
  );
  if (["ebay", "etsy", "shopify"].includes(marketplace)) {
    const guided = marketplaceProfileDefinition("guided", marketplace);
    return guided ? [...profiles, { ...guided, marketplace }] : profiles;
  }
  if (marketplace === "other") {
    const guided = marketplaceProfileDefinitions.find((profile) => profile.id === "guided");
    return guided ? [{ ...guided, marketplace }] : [];
  }
  return profiles;
}

export function marketplaceProfileDefinition(profile: MarketplaceConnectionProfile, marketplace?: Marketplace) {
  const definition = marketplaceProfileDefinitions.find((candidate) => candidate.id === profile);
  if (!definition || (marketplace && definition.marketplace !== marketplace && definition.id !== "guided")) {
    return undefined;
  }
  if (definition.id === "guided" && marketplace === "ebay") {
    return {
      ...definition,
      marketplace,
      label: "Work manually",
      description: "Prepare listing details in Listwork, then publish them yourself."
    };
  }
  return definition;
}

export function marketplaceAccountProfile(account: MarketplaceAccount) {
  return account.connectionProfile ?? defaultMarketplaceProfile(account.marketplace);
}

export function marketplaceAccountAllowsDirectPublishing(account: MarketplaceAccount) {
  const definition = marketplaceProfileDefinition(marketplaceAccountProfile(account), account.marketplace);
  if (!definition?.direct || !definition.capabilities.directPublishing) {
    return false;
  }
  return (
    account.connectionStatus === "connected" &&
    account.integrationMode === "api" &&
    account.lifecycleState === "ready" &&
    (account.approvalState === "approved" || account.approvalState === "not_required") &&
    account.authorizationState === "authorized" &&
    (account.configurationState === "valid" || account.configurationState === "not_required") &&
    (account.webhookState === "active" || account.webhookState === "not_required") &&
    (account.initialSyncState === "completed" || account.initialSyncState === "not_required") &&
    account.disconnectState !== "pending" &&
    account.disconnectState !== "failed" &&
    Boolean(account.provider && account.environment)
  );
}

export function marketplaceCapabilitiesFor(account: MarketplaceAccount) {
  return marketplaceProfileDefinition(marketplaceAccountProfile(account), account.marketplace)?.capabilities ?? guidedCapabilities;
}
